(() => {
  if (customElements.get('authris-live-price')) return;
  const cache = new Map();
  const pending = new Map();
  const widgets = new Set();
  const ttl = 60000;
  let timer;
  let running = false;
  const toMinor = (amount) => Math.round(Number(amount) * 100);
  const keyFor = (id, country) => `${country}:${id}`;

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(flush, 35);
  }

  async function flush() {
    if (running || !pending.size) return;
    running = true;
    const entries = [...pending.entries()].slice(0, 20);
    entries.forEach(([key]) => pending.delete(key));
    const country = entries[0][1].country;
    const group = entries.filter(([, item]) => item.country === country);
    entries.filter(([, item]) => item.country !== country).forEach(([key, item]) => pending.set(key, item));
    const variables = { country };
    const declarations = ['$country: CountryCode!'];
    const fields = group.map(([, item], index) => {
      variables[`v${index}`] = `gid://shopify/ProductVariant/${item.id}`;
      declarations.push(`$v${index}: ID!`);
      return `q${index}: cartCreate(input: {buyerIdentity: {countryCode: $country}, lines: [{merchandiseId: $v${index}, quantity: 1}]}) {
        cart { lines(first: 1) { nodes {
          merchandise { ... on ProductVariant { id } }
          cost { amountPerQuantity { amount currencyCode } }
          discountAllocations {
            __typename
            ... on CartAutomaticDiscountAllocation {
              title discountedAmount { amount currencyCode }
              sourceDiscountApplication { targetType value { ... on PricingPercentageValue { percentage } } }
            }
          }
        } } }
        userErrors { message }
      }`;
    });
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 12000);
      let response;
      try {
        response = await fetch('/api/2026-10/graphql.json', {
          method: 'POST', credentials: 'omit', signal: controller.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ query: `mutation AuthrisLivePrices(${declarations.join(',')}) { ${fields.join('\n')} }`, variables })
        });
      } finally {
        clearTimeout(timeout);
      }
      if (!response.ok) throw new Error('Price quote unavailable');
      const result = await response.json();
      if (result.errors?.length) throw new Error('Price quote unavailable');
      group.forEach(([key, item], index) => {
        const payload = result.data?.[`q${index}`];
        const line = payload?.cart?.lines?.nodes?.[0];
        let quote = null;
        if (!payload?.userErrors?.length && line?.merchandise?.id === `gid://shopify/ProductVariant/${item.id}`) {
          const currency = line.cost.amountPerQuantity.currencyCode;
          const base = toMinor(line.cost.amountPerQuantity.amount);
          const allocations = line.discountAllocations.filter((allocation) =>
            allocation.__typename === 'CartAutomaticDiscountAllocation' &&
            allocation.sourceDiscountApplication.targetType === 'LINE_ITEM' &&
            allocation.discountedAmount.currencyCode === currency
          );
          const saving = allocations.reduce((sum, allocation) => sum + toMinor(allocation.discountedAmount.amount), 0);
          const percentage = allocations.length === 1 ? allocations[0].sourceDiscountApplication.value.percentage : null;
          quote = { base, final: Math.max(0, base - saving), saving, currency, percentage, checked: Date.now() };
        }
        cache.set(key, { quote, checked: Date.now() });
        item.listeners.forEach((widget) => widget.apply(quote, item.id));
      });
    } catch {
      group.forEach(([key, item]) => {
        cache.set(key, { quote: null, checked: Date.now() - ttl + 15000 });
        item.listeners.forEach((widget) => widget.apply(null, item.id));
      });
    } finally {
      running = false;
      if (pending.size) schedule();
    }
  }

  class AuthrisLivePrice extends HTMLElement {
    connectedCallback() {
      widgets.add(this);
      this.onSelect = () => setTimeout(() => this.refresh(), 0);
      this.bundle = this.closest('bundle-product');
      this.bundle?.addEventListener('change', this.onSelect);
      this.refresh();
    }

    disconnectedCallback() {
      widgets.delete(this);
      this.bundle?.removeEventListener('change', this.onSelect);
    }

    selection() {
      const select = this.bundle?.querySelector('select');
      const option = select?.selectedOptions[0];
      return {
        id: select?.value || this.dataset.variant,
        base: option ? Number(option.dataset.price) : Number(this.dataset.base),
        available: option ? option.dataset.available === 'true' : this.dataset.available === 'true'
      };
    }

    refresh() {
      const selected = this.selection();
      if (!selected.id || !selected.available) return this.apply(null, selected.id);
      const country = this.dataset.country;
      const key = keyFor(selected.id, country);
      const cached = cache.get(key);
      if (cached && Date.now() - cached.checked < ttl) return this.apply(cached.quote, selected.id);
      const entry = pending.get(key) || { id: selected.id, country, listeners: new Set() };
      entry.listeners.add(this);
      pending.set(key, entry);
      schedule();
    }

    money(amount) {
      if (window.theme?.Currency?.formatMoney) return theme.Currency.formatMoney(amount, this.dataset.moneyFormat);
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency
      }).format(amount / 100);
    }

    apply(quote, variantId) {
      if (!this.isConnected || this.selection().id !== variantId) return;
      const selected = this.selection();
      const active = !!quote && selected.available && quote.currency === this.dataset.currency &&
        quote.base === selected.base && quote.saving > 0 && quote.final < selected.base;
      this.toggleAttribute('data-auto-active', active);
      const display = this.querySelector(':scope > .authris-live-price__display');
      display.hidden = !active;
      if (active) {
        if (!display.childElementCount) {
          display.innerHTML = '<dl><div class="price__sale"><dt class="visually-hidden" data-auto-sale-label></dt><dd><span class="price-item price-item--sale"><span data-auto-from></span><price-money><bdi data-auto-final></bdi></price-money></span></dd><dt class="visually-hidden" data-auto-regular-label></dt><dd><s class="price-item price-item--regular"><price-money><bdi data-auto-original></bdi></price-money></s></dd></div></dl><span class="badge price__badge-sale" data-auto-badge></span>';
        }
        display.querySelector('[data-auto-final]').innerHTML = this.money(quote.final);
        display.querySelector('[data-auto-original]').innerHTML = this.money(quote.base);
        display.querySelector('[data-auto-from]').textContent = this.dataset.from === 'true' ? `${this.dataset.fromLabel} ` : '';
        display.querySelector('[data-auto-sale-label]').textContent = this.dataset.saleLabel;
        display.querySelector('[data-auto-regular-label]').textContent = this.dataset.regularLabel;
        const badge = display.querySelector('[data-auto-badge]');
        badge.hidden = this.dataset.showBadge === 'false';
        badge.innerHTML = Number.isFinite(quote.percentage) ? `−${quote.percentage}%` : `−${this.money(quote.saving)}`;
        this.dataset.final = String(quote.final);
      } else {
        delete this.dataset.final;
      }
      this.dispatchEvent(new CustomEvent('authris:auto-price', { bubbles: true, detail: {
        productId: this.dataset.product, active, variantId, final: active ? quote.final : selected.base
      } }));
    }
  }

  class AuthrisAutoPriceNote extends HTMLElement {
    connectedCallback() {
      this.onPrice = (event) => {
        if (event.detail.productId === this.dataset.product) this.hidden = !event.detail.active;
      };
      document.addEventListener('authris:auto-price', this.onPrice);
      const info = this.closest('product-info');
      this.hidden = !info?.querySelector('authris-live-price[data-auto-active]');
    }
    disconnectedCallback() { document.removeEventListener('authris:auto-price', this.onPrice); }
  }

  customElements.define('authris-live-price', AuthrisLivePrice);
  customElements.define('authris-auto-price-note', AuthrisAutoPriceNote);
  setInterval(() => {
    if (document.visibilityState === 'visible') widgets.forEach((widget) => widget.refresh());
  }, ttl);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') widgets.forEach((widget) => widget.refresh());
  });
})();
